
import {
  Component, Ref, Vue, Watch,
} from 'vue-property-decorator'
import Widget from '@/components/Widget/Widget.vue'
import MediaPlan from '@/models/MediaPlan'
import ReconciliationGroup from '@/models/ReconciliationGroup'
import ViewModel from '@/models/ViewModel'
import IconAction from '@/components/IconAction/IconAction.vue'
import SearchInput from '@/components/SearchInput/SearchInput.vue'
import DataTable from '@/components/DataTable/index.vue'
import SelectPicker from '@/components/SelectPicker/SelectPicker.vue'
import { clone, identity } from 'lodash'
import ExportMediaPlan from '@/pages/Sales/MediaPlan/components/ExportMediaPlan.vue'
import { getModule } from 'vuex-module-decorators'
import SystemtModule from '@/store/SystemModule'
import ReconciliationMonthly from '@/models/ReconciliationMonthly'
import DataForm from '@/components/DataForm/DataForm.vue'
import { watch } from 'fs'
import WebMessage from '@/models/WebMessage'
import FaqModal from '@/components/FaqModal/FaqModal.vue'
import options from './options'
import formData from './form'
import MediaPlanBatchEdit from './components/MediaPlanBatchEdit.vue'

@Component({
  components: {
    Widget,
    IconAction,
    SearchInput,
    DataTable,
    SelectPicker,
    DataForm,
    MediaPlanBatchEdit,
    FaqModal,
  },
})
export default class MediaPlanHome extends ViewModel {
  // dataTable Ref
  @Ref() readonly dataTable!: HTMLFormElement

  @Ref() readonly batchForm: any

  // Target Media Plan during action event
  public media_plan: MediaPlan = new MediaPlan()

  // Total Number of records
  public records: number = 0

  // Loading incicator
  public loading: boolean = false

  public show_filter_helper: boolean = false

  private busy = false

  // Search Input
  public query: string[] = []

  // Selected rows
  public selected: string[] = []

  // dataTable field filters
  public fieldFilters: any = {
    created_at: '',
    updated_at: '',
    type: '',
    status: '',
    lost_category: '',
    products: '',
  }

  // table data
  public table_data: MediaPlan[] = []

  // Used to determine which detail view to show
  public detail_mode = 'schedule'

  // Indicates is component is initialized and ready to load data
  public ready = false

  public temp_mediaplan_batch = new MediaPlan({ status: 'pending_review' })

  public temp_batch_tab: number = 0

  public formFields: any = formData

  public batch_item_modal: boolean = false

  public batch_data: any = []

  public filter_mode: string = 'exclusive'

  @Watch('filter_mode')
  public onFilterModeChange() {
    this.refresh()
  }

  @Watch('fields', { deep: true })
  public onFieldsChange() {
    if (this.ready) {
      this.syncFilters()
    }
  }

  // Media Plan Module Options (Search options, Action options, etc)
  public get options() {
    return options
  }

  // List of fields, populated on component mount
  public fields: Array<any> = []

  // Return active fields to the table
  public get show_fields() {
    return this.fields.filter((f: any) => f.show)
  }

  public get active_field_keys() {
    return this.show_fields.map((f: any) => f.key)
  }

  // Return Promise with DataTable data
  public rows(context: any): Promise<any> {
    this.loading = true
    const field_filters = Object.keys(this.fieldFilters)
      .filter((key: string) => this.fieldFilters[key] !== '')
      .map((key: string) => `${key}:${this.fieldFilters[key].toLowerCase()}`)

    this.syncFilters()
    return MediaPlan.paginate({
      page_size: context.perPage,
      page: context.currentPage,
      order_by: context.sortBy,
      order: context.sortDesc ? 'desc' : 'asc',
      query: [...context.filter, ...field_filters],
      mode: this.filter_mode,
    }).then(result => {
      this.records = result.records
      this.selected = []
      this.loading = false
      this.table_data = result.data
      return result.data
    })
  }

  // Redirect user to the create page
  public createMediaPlan() {
    this.$router.push('/app/sales/media_plan')
  }

  // Refresh dataTable
  public refresh() {
    this.dataTable.refresh()
  }

  // Reset filters
  public resetFilters() {
    this.fieldFilters = {
      created_at: '',
      updated_at: '',
      type: '',
      status: '',
      products: '',
    }
    this.query = []
    this.filter_mode = 'exclusive'
    this.clearFilters()
    this.refresh()
  }

  // Initialize vars
  public mounted() {
    this.fields = clone(MediaPlan.tableFields)
    this.loadFilters()
  }

  // Store session filters in VUEX
  public syncFilters() {
    const system = getModule(SystemtModule)
    system.updateState({
      name: 'filters',
      type: 'media_plan',
      data: { query: this.query, fieldFilters: this.fieldFilters, mode: this.filter_mode },
    })

    system.updateState({
      name: 'fields',
      type: 'media_plan',
      data: this.active_field_keys,
    })
  }

  // Load filters from VUEX if present
  public loadFilters() {
    const system = getModule(SystemtModule)
    let promises: Promise<any>[] = []

    const filter = system.getFilter('media_plan').then((filter: any) => {
      if (filter) {
        this.query = filter.query
        this.fieldFilters = filter.fieldFilters
        this.filter_mode = filter.mode
      }
    })

    const field = system.getFields('media_plan').then((fields: any) => {
      if (fields) {
        this.fields = this.fields.map((field: any) => {
          field.show = fields.includes(field.key)
          return field
        })
      }
    })

    promises.push(filter)
    promises.push(field)

    Promise.all(promises).then(() => {
      this.ready = true
    })
  }

  // Clear VUEX filters state
  public clearFilters() {
    const system = getModule(SystemtModule)
    system.updateState({
      name: 'filters',
      type: 'media_plan',
      data: null,
    })
  }

  // Open Batch export dialog
  public exportMediaPlans() {
    if (this.selected.length === 0) {
      WebMessage.error('Please select at least one Media Plan')
      return
    }
    let Component = Vue.extend(ExportMediaPlan)
    let instance = new Component({
      propsData: {
        media_plans: this.table_data.filter(
          (media_plan: MediaPlan) =>
            typeof media_plan.id === 'string' && this.selected.includes(media_plan.id),
        ),
        mode: 'single',
      },
    })
    instance.$mount()
  }

  public batchEdit() {
    if (this.selected.length === 0) {
      WebMessage.error('Please select at least one Media Plan')
      return
    }
    this.$bvModal.show('batch-edit')
  }

  public addLineItem() {
    this.temp_mediaplan_batch.addLineItem()
  }

  public async batchConfirm() {
    this.loading = true
    this.batch_item_modal = false
    let obj = this.batchForm.buildData()

    // Build the object containing only the selected fields
    let selected_mediaplan_fields = obj.selected.filter((sel: any) => sel.startsWith('mediaplan.'))
    let selected_lineitems_fields = obj.selected.filter((sel: any) => sel.startsWith('lineitem.'))
    let selected_metadata_fields = obj.selected.filter((sel: any) => sel.startsWith('metadata.'))

    let selected: any = this.table_data.filter((mediaplan: any) =>
      this.selected.includes(mediaplan.id))
    // map deep in the mediaplan object
    selected = selected.map((mediaplan: any) => {
      // root mediaplan object format
      selected_mediaplan_fields.forEach((prop: any) => {
        const path = prop.replaceAll('mediaplan.', '').split('.')

        if (path.length === 1) {
          mediaplan[path[0]] = obj.media_plan[path[0]]
        }
        if (path.length === 2) {
          mediaplan[path[0]][path[1]] = obj.media_plan[path[0]][path[1]]
        }
        if (path.length === 3) {
          mediaplan[path[0]][path[1]][path[2]] = obj.media_plan[path[0]][path[1]][path[2]]
        }
      })
      // mediaplan.metadata object format
      selected_metadata_fields.forEach((prop: any) => {
        const path = prop.replaceAll('metadata.', '').split('.')

        if (path.length === 1) {
          mediaplan.metadata[path[0]] = obj.media_plan.metadata[path[0]]
        }
        if (path.length === 2) {
          mediaplan.metadata[path[0]][path[1]] = obj.media_plan.metadata[path[0]][path[1]]
        }
        if (path.length === 3) {
          mediaplan.metadata[path[0]][path[1]][path[2]] = obj.media_plan.metadata[path[0]][path[1]][path[2]]
        }
      })
      // mediaplan.lineitems[] object format
      mediaplan.line_items = mediaplan.line_items.map((item: any) => {
        selected_lineitems_fields.forEach(prop => {
          const path = prop.replaceAll('lineitem.', '').split('.')

          if (path.length === 1) {
            item[path[0]] = obj.media_plan.line_items[0][path[0]]
          }
          if (path.length === 2) {
            item[path[0]][path[1]] = obj.media_plan.line_items[0][path[0]][path[1]]
          }
          if (path.length === 3) {
            item[path[0]][path[1]][path[2]] = obj.media_plan.line_items[0][path[0]][path[1]][path[2]]
          }
        })

        return item
      })

      return mediaplan
    })

    MediaPlan.batchEdit(selected)
      .then(response => {
        WebMessage.success(
          'Batch update complete! Please review the changes to ensure that it was applied correctly to the media plans.',
        )
        setTimeout(() => {
          this.$bvModal.hide('batch-edit')
          this.loading = false
        }, 2000)
        setTimeout(() => {
          this.refresh()
        }, 2500)
      })
      .catch(error => {
        this.loading = false
      })
  }
}
